import { gridActions } from '@oplog/data-grid';
import { Box, Button, Flex, Icon, Input } from '@oplog/express';
import { Resource, resourceActions } from '@oplog/resource-redux';
import React, { useEffect, useState } from 'react';
import BarcodeReader from 'react-barcode-reader';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { isBarcodeDebuggingEnabled } from '../../../../config';
import { GridType, ResourceType } from '../../../../models';
import { urls } from '../../../../routers/urls';
import {
  AddressTypeOutputDTO,
  InternalErrorNumber,
  PrintApprovedCargoPackageCarrierLabelOutputDTO,
} from '../../../../services/swagger';
import { StoreState } from '../../../../store/initState';
import MoreActionScreen from '../../../molecules/MoreActionScreen/MoreActionScreen';
import ReturnDialogModal from '../../../molecules/ReturnDialogModal/ReturnDialogModal';
import { StationBox } from '../../../molecules/TouchScreen';
import { DiscriminatorTypes } from '../../../molecules/TouchScreen/StationBox';
import ManuelBarcodeInput from '../../../organisms/ManuelBarcodeInput';
import { StatusEnum } from '../../SlamStation/bones/BarcodeIcon';
import PackageScanInfo from '../../SlamStation/bones/PackageScanInfo';
import ScanStatusBox from '../../SlamStation/bones/ScanStatusBox';
import ReprintModal from './ReprintModal';
import StatusModal from './StatusModal';

export enum ScanPackageModals {
  Logout = 'Logout',
  Print = 'Print',
  Reprint = 'Reprint',
  Status = 'Status',
}

const intlKey = 'TouchScreen.SlamStation';

const ScanPackageColumn = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [station, setStation] = useState<AddressTypeOutputDTO>({ id: 0, label: '', discriminator: '' });
  const [status, setStatus] = useState<StatusEnum>(StatusEnum.Initial);
  const [isMoreActionsOpen, setIsMoreActionsOpen] = useState<boolean>(false);
  const [isManuelBarcodeInputOpen, setIsManuelBarcodeInputOpen] = useState<boolean>(false);
  const [cargoPackageBarcodeToReprint, setCargoPackageBarcodeToReprint] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [modal, setModal] = useState<Record<ScanPackageModals, boolean>>({
    Logout: false,
    Print: false,
    Reprint: false,
    Status: false,
  });
  const toggleModalState = (name: ScanPackageModals, state?: boolean) =>
    setModal({ ...modal, [name]: state === undefined ? !modal[name] : state });

  const printApprovedCargoPackageCarrierLabelResponse: Resource<PrintApprovedCargoPackageCarrierLabelOutputDTO> =
    useSelector((state: StoreState) => state.resources[ResourceType.PrintApprovedCargoPackageCarrierLabel]);

  useEffect(() => {
    const stationString = localStorage.getItem('stationAddress');
    const stationObject = typeof stationString === 'string' ? JSON.parse(stationString) : undefined;
    if (stationObject && stationObject.discriminator === DiscriminatorTypes.ApprovedSLAMAddress) {
      setStation(stationObject);
    } else {
      navigate(urls.stationLogin);
    }
    dispatch(resourceActions.resourceRequested(ResourceType.ListCarriers));
    return () => {
      dispatch(resourceActions.resourceInit(ResourceType.ListCarriers));
      dispatch(resourceActions.resourceInit(ResourceType.PrintApprovedCargoPackageCarrierLabel));
      dispatch(gridActions.gridStateCleared(GridType.ApprovedCargoPackagesGrid));
    };
  }, []);

  useEffect(() => {
    if (printApprovedCargoPackageCarrierLabelResponse?.isBusy) return setStatus(StatusEnum.Loading);

    if (printApprovedCargoPackageCarrierLabelResponse?.isSuccess) {
      dispatch(gridActions.gridFetchRequested(GridType.ApprovedCargoPackagesGrid));
      return setStatus(StatusEnum.Success);
    }

    if (printApprovedCargoPackageCarrierLabelResponse?.error) {
      switch (printApprovedCargoPackageCarrierLabelResponse.error?.internalErrorNumber) {
        case InternalErrorNumber.SlamCargoPackageLabelUrlError:
          setStatus(StatusEnum.Initial);
          return toggleModalState(ScanPackageModals.Print, true);
        case InternalErrorNumber.SlamCargoPackageReadyToShipError:
          setStatus(StatusEnum.Initial);
          return toggleModalState(ScanPackageModals.Reprint, true);
        case InternalErrorNumber.SlamCargoPackageDispatchedError:
          setMessage(t(`${intlKey}.Modals.DispatchCompleted`));
          break;
        case InternalErrorNumber.SlamCargoPackageShippingDomesticError:
          setMessage(t(`${intlKey}.Modals.PrintedAtPacking`));
          break;
        case InternalErrorNumber.SlamSalesOrderMustBeApproved:
          setMessage(t(`${intlKey}.Modals.SalesOrderMustBeApproved`));
          break;
        case InternalErrorNumber.SlamInvalidAddressForSalesOrder:
          setMessage(t(`${intlKey}.Modals.InvalidAddressForApprovedSlam`));
          break;
        case InternalErrorNumber.SlamSalesOrderPackingMustBeCompleted:
          setMessage(t(`${intlKey}.Modals.PackingMustBeCompleted`));
          break;
        default:
          return setStatus(StatusEnum.WrongBarcode);
      }
      setStatus(StatusEnum.Initial);
      return toggleModalState(ScanPackageModals.Status, true);
    }
  }, [printApprovedCargoPackageCarrierLabelResponse]);

  const handleBarcodeScan = (data: string) => {
    data = data.trim();
    if (
      printApprovedCargoPackageCarrierLabelResponse?.isBusy ||
      isMoreActionsOpen ||
      Object.values(modal).some((isOpen: boolean) => isOpen)
    )
      return;
    setCargoPackageBarcodeToReprint(data);
    dispatch(
      resourceActions.resourceRequested(ResourceType.PrintApprovedCargoPackageCarrierLabel, {
        params: { addressLabel: station.label, cargoPackageBarcode: data, isForcePrint: false },
      })
    );
  };

  return (
    <>
      <BarcodeReader onScan={handleBarcodeScan} avgTimeByChar={100} minLength={2} />
      <Flex fontFamily="Jost" bg="palette.slate_lighter" flexDirection="column" height="100vh" p={22}>
        <StationBox station={station} />
        <ScanStatusBox status={status}>
          <PackageScanInfo status={status} response={printApprovedCargoPackageCarrierLabelResponse} />
        </ScanStatusBox>
        <Flex
          justifyContent="space-evenly"
          p={11}
          fontSize={22}
          borderRadius="lg"
          bg="palette.softGrey"
          color={`palette.${status === StatusEnum.Success ? 'slate_darker' : 'grey_lighter'}`}
          my={14}
          fontWeight={500}
          alignItems="center"
        >
          <Icon name="fad fa-barcode-scan" />
          <Box lineHeight="xxSmall" letterSpacing="small">
            {t(`${intlKey}.ScanBox.ScanNextPackage`)}
          </Box>
        </Flex>
        <Flex justifyContent="space-between" position="absolute" width={1 / 3} mt={8} bottom={16}>
          <Button variant="light" outline="none !important" onClick={() => setIsMoreActionsOpen(true)}>
            <Icon name="fas fa-ellipsis-v" color="palette.softBlue" />
          </Button>
          {isMoreActionsOpen && (
            <MoreActionScreen
              setIsMoreActionsOpen={setIsMoreActionsOpen}
              setIsManuelBarcodeInputOpen={setIsManuelBarcodeInputOpen}
              toggleModalState={toggleModalState}
            />
          )}
          {isBarcodeDebuggingEnabled && (
            <Input
              onChange={e => handleBarcodeScan(e.target.value)}
              zIndex={5000}
              width={120}
              mr={44}
              height={32}
              autoFocus
            />
          )}
        </Flex>
      </Flex>
      {isManuelBarcodeInputOpen && (
        <ManuelBarcodeInput
          placeholder={t(`${intlKey}.ManualBarcodeInput.PackageNumberPlaceholder`)}
          closeScreenKeyboard={() => setIsManuelBarcodeInputOpen(false)}
          getBarcodeDataFromScreenKeyboard={data => handleBarcodeScan(data)}
        />
      )}
      {modal.Logout && (
        <ReturnDialogModal
          modals={modal}
          toggleModalState={toggleModalState}
          type={`TouchScreen.LogoutModal.Types.CargoLabeling`}
        />
      )}
      {(modal.Print || modal.Reprint) && (
        <ReprintModal
          setIsOpen={toggleModalState}
          isReprint={modal.Reprint}
          station={station}
          cargoPackageBarcode={cargoPackageBarcodeToReprint}
        />
      )}
      {modal.Status && <StatusModal setIsOpen={toggleModalState} message={message} />}
    </>
  );
};

export default ScanPackageColumn;
